import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Collections } from '@graylog/enterprise-api';

import UserNotification from 'util/UserNotification';
import type { Collection, CollectionEntity } from 'collections/types';
import { COLLECTIONS_QUERY_KEY } from 'collections/hooks/useCollections';
import { COLLECTION_ENTITIES_QUERY_KEY } from 'collections/hooks/useCollectionEntities';
import { COLLECTION_CANDIDATES_QUERY_KEY } from 'collections/hooks/useCollectionCandidates';

type UpdateMutationParams = {
  collectionId: Collection['id'];
  addedEntities?: Array<CollectionEntity['id']>;
  removedEntities?: Array<CollectionEntity['id']>;
};

const updateCollectionEntities = async ({
  collectionId,
  addedEntities = [],
  removedEntities = [],
}: UpdateMutationParams) =>
  Collections.updateEntities(collectionId, { added_entities: addedEntities, removed_entities: removedEntities });

const bulkAddEntities = async ({
  collectionId,
  addedEntities = [],
}: Pick<UpdateMutationParams, 'collectionId' | 'addedEntities'>) =>
  Collections.bulkAdd({ entity_ids: addedEntities }, collectionId);

const bulkRemoveEntities = async ({
  collectionId,
  removedEntities = [],
}: Pick<UpdateMutationParams, 'collectionId' | 'removedEntities'>) =>
  Collections.bulkDeleteBycollectionIdAndEntitiestoremove({ entity_ids: removedEntities }, collectionId);

const useCollectionEntityMutation = (collectionId: Collection['id']) => {
  const queryClient = useQueryClient();

  const add = useMutation({
    mutationFn: ({ addedEntities }: { addedEntities: UpdateMutationParams['addedEntities'] }) =>
      updateCollectionEntities({ collectionId, addedEntities }),

    onError: (errorThrown) => {
      UserNotification.error(`Adding entities failed with status: ${errorThrown}`, 'Could not add entities');
    },

    onSuccess: () => {
      UserNotification.success('Entities haven been successfully added.', 'Success!');

      queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY, collectionId], type: 'active' });
      queryClient.invalidateQueries({ queryKey: [COLLECTION_ENTITIES_QUERY_KEY, collectionId], type: 'active' });
      queryClient.invalidateQueries({ queryKey: [COLLECTION_CANDIDATES_QUERY_KEY, collectionId], type: 'active' });
    },
  });

  const remove = useMutation({
    mutationFn: ({ removedEntities }: { removedEntities: UpdateMutationParams['addedEntities'] }) =>
      updateCollectionEntities({ collectionId, removedEntities }),

    onError: (errorThrown) => {
      UserNotification.error(`Removing entities failed with status: ${errorThrown}`, 'Could not remove entities');
    },

    onSuccess: () => {
      UserNotification.success('Entities haven been successfully removed.', 'Success!');

      queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY, collectionId], type: 'active' });
      queryClient.invalidateQueries({ queryKey: [COLLECTION_ENTITIES_QUERY_KEY, collectionId], type: 'active' });
      queryClient.invalidateQueries({ queryKey: [COLLECTION_CANDIDATES_QUERY_KEY, collectionId], type: 'active' });
    },
  });

  const bulkAdd = useMutation({
    mutationFn: ({ addedEntities }: { addedEntities: UpdateMutationParams['addedEntities'] }) =>
      bulkAddEntities({ collectionId, addedEntities }),
  });

  const bulkRemove = useMutation({
    mutationFn: ({ removedEntities }: { removedEntities: UpdateMutationParams['removedEntities'] }) =>
      bulkRemoveEntities({ collectionId, removedEntities }),
  });

  return {
    addEntities: add.mutateAsync,
    removeEntities: remove.mutateAsync,
    bulkAddEntitiesMutation: bulkAdd,
    bulkRemoveEntitiesMutation: bulkRemove,
  };
};

export default useCollectionEntityMutation;
